/**
 * F-188 / T-165-002 — meeting-link CTA (US-165).
 *
 * Renders the per-tier booking link (config-built, target=_blank,
 * rel=noopener) and fires the T-165-003 confirmation trigger
 * (POST api/meeting-link/confirm) fire-and-forget on click, so the
 * LP side awaits the confirmation email without blocking navigation.
 */
import React from 'react';
import { getMeetingLinkUrl } from '../../config/meeting-link';
import { landlordApi } from '../../services/api';
import { trackEnterpriseInquiry, trackVariantEvent } from '../../services/analytics';
import { getAssignedVariant } from '../../services/experimentAssignment';

export interface MeetingLinkCtaProps {
  /** Plan tier driving plan_type routing (unknown tiers fall back to free/self-serve). */
  tier?: string | null;
  campaign?: string;
  abVariant?: string;
  label?: string;
  className?: string;
}

export default function MeetingLinkCta({
  tier,
  campaign,
  abVariant,
  label = 'Book a call',
  className,
}: MeetingLinkCtaProps) {
  const href = getMeetingLinkUrl({ tier, campaign, abVariant });

  const handleClick = () => {
    // Fail-soft confirmation: navigation must never wait on the mailer.
    landlordApi
      .post('api/meeting-link/confirm', {
        tier: tier ?? null,
        campaign: campaign ?? null,
        abVariant: abVariant ?? null,
      })
      .catch(() => {
        /* booking link still opens even if the API call fails */
      });
    // Batch 4 S6/S10: enterprise-inquiry + variant-tagged booking event.
    trackEnterpriseInquiry({ tier: tier ?? null, campaign: campaign ?? null, abVariant: abVariant ?? null });
    const variant = abVariant ?? getAssignedVariant('meeting-link');
    trackVariantEvent('booking_clicked', { variant, tier: tier ?? null, campaign: campaign ?? null });
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      data-testid="meeting-link-cta"
      className={className}
    >
      <i className="fas fa-calendar-check mr-2"></i>
      {label}
    </a>
  );
}
